import React, { useEffect, useState, useRef } from "react";
import {
  ClipboardCopy,
  DollarSign,
  Users,
  ExternalLink,
  TrendingUp,
  BarChart2,
  Activity,
  CheckCircle,
  CreditCard,
  History,
  AlertCircle,
  Banknote,
  LineChart as LineChartIcon,
} from "lucide-react";
import { formatCurrency } from "../../utils/currency";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Select } from "../../components/common/Select";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useGlobalState } from "../../GlobalStates";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getReferralAmount } from "../../utils/multi-language";
import { useUserLocale } from "../../customHooks/useUserLocale";
import { StickyHeader } from "../../intro/components/landing-new";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  Bar,
  ComposedChart,
} from "recharts";
import { Select as CustomSelect } from "../../components/common/Select";
import {
  getAffiliateAnalytics,
  getAffiliateReferrals,
  getAffiliateEarningsSummary,
  requestWithdrawal,
  getWithdrawals,
  getAffiliateBalances,
} from "../../services/affiliate";
import { useIsAuthenticated } from "../../utils/auth";
import dayjs from "dayjs";

const WITHDRAWAL_METHOD_MAP = {
  bank_transfer: {
    label: "Bank Transfer",
    icon: <CreditCard className="tw-w-4 tw-h-4 tw-text-blue-600" />,
    className: "tw-text-blue-600",
  },
};

const AffiliateProgram = () => {
  const [user] = useGlobalState("user");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currency } = useUserLocale();
  const { t } = useTranslation();
  const { isLoading: loadingAuth } = useIsAuthenticated();
  const [inviteUrl, setInviteUrl] = useState("www.sotravel.com/welcome-discount");
  const [inviteMessage, setInviteMessage] = useState("");
  const [copyIconColor, setCopyIconColor] = useState("black");
  const [_, setOpenAlertBannerMsg] = useGlobalState("openAlertBannerMsg");

  const [analyticsData, setAnalyticsData] = useState({
    daily: [],
    weekly: [],
    monthly: [],
    all_time: [],
  });
  const [referrals, setReferrals] = useState([]);
  const [referralTotal, setReferralTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  const [earningsSummary, setEarningsSummary] = useState({
    totalEarnings: "0.00",
    totalClicks: 0,
    totalSignups: 0,
    totalBookings: 0,
    signupRate: "0.0",
    bookingRate: "0.0",
  });

  const timeframe = searchParams.get("timeframe") || "all_time";
  const setTimeframe = (newTimeframe) => {
    setSearchParams({ ...Object.fromEntries(searchParams), timeframe: newTimeframe });
  };

  const [isLoading, setIsLoading] = useState(true);
  const canAccessWithdrawal = user?.influencer_type === "Popular duck";

  const changeCopyColor = () => {
    setOpenAlertBannerMsg(t("affiliate.affiliate_link.copied"));
    setCopyIconColor("deepskyblue");
    setTimeout(() => setCopyIconColor("black"), 1200);
  };

  useEffect(() => {
    const getUserInviteLink = async () => {
      if (user.id) {
        const inviteUrl = `www.sotravel.com/welcome-discount?code=${user.id}`;
        const msg = t("invite_message", {
          defaultValue: `Use this link to get {{amount}} off your first Sotravel trip! 👉 ${inviteUrl}`,
          amount: getReferralAmount(currency, "welcome"),
          inviteUrl: inviteUrl,
        });
        setInviteMessage(msg);
        setInviteUrl(inviteUrl);
      }
    };
    getUserInviteLink();
  }, [user.id, currency, t]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user.id) return;
      setIsLoading(true);
      try {
        const { data: analytics } = await getAffiliateAnalytics(timeframe);
        const formattedAnalytics = analytics.map((item) => ({
          date: item.date || "",
          clicks: parseInt(item.clicks || 0),
          signups: parseInt(item.signups || 0),
          bookings: parseInt(item.bookings || 0),
          earningsCash: parseFloat(item.earningsCash || 0).toFixed(2),
          earningsCredits: parseFloat(item.earningsCredits || 0).toFixed(2),
          signupRate: item.signupRate || "0.0",
          bookingRate: item.bookingRate || "0.0",
        }));
        setAnalyticsData((prevData) => ({ ...prevData, [timeframe]: formattedAnalytics }));

        const { data: referralsData } = await getAffiliateReferrals({ limit: pageSize, offset: currentPage * pageSize });
        setReferrals(referralsData.referrals);
        setReferralTotal(referralsData.total);

        const { data: summary } = await getAffiliateEarningsSummary();
        setEarningsSummary({
          totalEarnings: parseFloat(summary.totalEarnings || 0).toFixed(2),
          totalClicks: parseInt(summary.totalClicks || 0),
          totalSignups: parseInt(summary.totalSignups || 0),
          totalBookings: parseInt(summary.totalBookings || 0),
          signupRate: summary.signupRate || "0.0",
          bookingRate: summary.bookingRate || "0.0",
        });
      } catch (error) {
        console.error("Error fetching affiliate data:", error);
        setOpenAlertBannerMsg("Failed to load affiliate data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [user.id, timeframe, currentPage, setOpenAlertBannerMsg]);

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  if (loadingAuth) return <LoadingSkeleton />;

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <StickyHeader withoutCta />
      <HeroSection user={user} navigate={navigate} t={t} />
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-8">
        {!user.id ? (
          <>
            <HowItWorksSection currency={currency} t={t} />
            <BenefitsSection currency={currency} t={t} />
          </>
        ) : (
          <>
            <AffiliateLinkSection
              inviteUrl={inviteUrl}
              inviteMessage={inviteMessage}
              changeCopyColor={changeCopyColor}
              copyIconColor={copyIconColor}
              t={t}
            />
            <AnalyticsSection
              isLoading={isLoading}
              timeframe={timeframe}
              setTimeframe={setTimeframe}
              analyticsData={analyticsData}
              earningsSummary={earningsSummary}
              referrals={referrals}
              referralTotal={referralTotal}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              t={t}
            />
            {/* {canAccessWithdrawal && (
              <WithdrawalSection
                earningsSummary={earningsSummary}
                setOpenAlertBannerMsg={setOpenAlertBannerMsg}
                t={t}
              />
            )} */}
          </>
        )}
        <footer className="tw-mt-12 tw-text-center tw-border-t tw-border-gray-200 tw-pt-8">
          <p className="tw-text-gray-600">{t("affiliate.footer", { defaultValue: "Need help? Our support team is here to assist you 24/7!" })}</p>
        </footer>
      </div>
    </div>
  );
};

const ReferralsTable = ({ referrals, total, currentPage, onPageChange, pageSize, t }) => {
  const getAffiliateSourceText = (source) => {
    switch (source) {
      case "affiliate_link": return t("affiliate.analytics.referrals.columns.source_affiliate_link", { defaultValue: "Affiliate link" });
      case "promo_affiliate": return t("affiliate.analytics.referrals.columns.source_promo_affiliate", { defaultValue: "Promo Code" });
      default: return source;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "signed_up": return "tw-bg-purple-50 tw-text-purple-600";
      case "booking": return "tw-bg-green-50 tw-text-green-600";
      case "cancelled": return "tw-bg-red-50 tw-text-red-600";
      default: return "tw-bg-gray-50 tw-text-gray-600";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "signed_up": return t("affiliate.analytics.referrals.status.signed_up");
      case "booking": return t("affiliate.analytics.referrals.status.completed_booking");
      case "cancelled": return t("affiliate.analytics.referrals.status.cancelled", { defaultValue: "Cancelled" });
      default: return t("affiliate.analytics.referrals.status.unknown", { defaultValue: "Unknown" });
    }
  };

  const formatCommission = (amount, currency, claim_type) => {
    if (!claim_type) return t("affiliate.analytics.referrals.columns.na", { defaultValue: "N/A" });
    return formatCurrency(amount, currency);
  };

  const totalPages = Math.ceil(total / pageSize);

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    range.push(1);
    for (let i = currentPage - delta; i <= currentPage + delta + 1; i++) {
      if (i < totalPages && i > 1) range.push(i);
    }
    if (totalPages > 1) range.push(totalPages);

    for (let i of range) {
      if (l) {
        if (i - l === 2) rangeWithDots.push(l + 1);
        else if (i - l !== 1) rangeWithDots.push("...");
      }
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  };

  return (
    <div className="tw-space-y-4">
      <div className="tw-w-full tw-overflow-x-auto">
        <table className="tw-w-full tw-border-collapse tw-table-fixed">
          <colgroup>
            <col className="tw-w-[100px]" />
            <col className="tw-w-[120px]" />
            <col className="tw-w-[250px]" />
            <col className="tw-w-[120px]" />
            <col className="tw-w-[120px]" />
            <col className="tw-w-[100px]" />
            <col className="tw-w-[120px]" />
          </colgroup>
          <thead>
            <tr className="tw-border-b tw-border-gray-200">
              <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.analytics.referrals.columns.status")}</th>
              <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.analytics.referrals.columns.name")}</th>
              <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.analytics.referrals.columns.trip", { defaultValue: "Trip" })}</th>
              <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.analytics.referrals.columns.source", { defaultValue: "Source" })}</th>
              <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.analytics.referrals.columns.date")}</th>
              <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.analytics.referrals.columns.claimed", { defaultValue: "Claimed" })}</th>
              <th className="tw-text-right tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.analytics.referrals.columns.earned")}</th>
            </tr>
          </thead>
          <tbody>
            {referrals.map((referral, index) => (
              <motion.tr
                key={referral.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 tw-transition-colors"
              >
                <td className="tw-py-4 tw-px-4 tw-whitespace-nowrap">
                  <span className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${getStatusColor(referral.status)}`}>
                    {getStatusText(referral.cancelled ? "cancelled" : referral.status)}
                  </span>
                </td>
                <td className="tw-py-4 tw-px-4 tw-text-sm tw-text-gray-900 tw-truncate">{referral.user}</td>
                <td className="tw-py-4 tw-px-4 tw-text-sm tw-text-gray-900">
                  <div className="tw-truncate" title={referral.trip || t("affiliate.analytics.referrals.columns.no_trip", { defaultValue: "No trip booked" })}>
                    {referral.trip || t("affiliate.analytics.referrals.columns.no_trip", { defaultValue: "No trip booked" })}
                  </div>
                </td>
                <td className="tw-py-4 tw-px-4 tw-text-sm tw-text-gray-600 tw-whitespace-nowrap">{getAffiliateSourceText(referral.affiliate_source)}</td>
                <td className="tw-py-4 tw-px-4 tw-text-sm tw-whitespace-nowrap">
                  {referral.bookingDate ? dayjs(referral.bookingDate).format("DD MMM YYYY") : t("affiliate.analytics.referrals.columns.not_booked", { defaultValue: "Not booked" })}
                </td>
                <td className="tw-py-4 tw-px-4 tw-whitespace-nowrap">
                  {referral.claim_type ? (
                    <span className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${referral.claimed ? "tw-bg-green-50 tw-text-green-600" : "tw-bg-yellow-50 tw-text-yellow-600"}`}>
                      {referral.claimed ? t("affiliate.analytics.referrals.status.claimed", { defaultValue: "Claimed" }) : t("affiliate.analytics.referrals.status.pending")}
                    </span>
                  ) : (
                    <span className="tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-gray-50 tw-text-gray-600">
                      {t("affiliate.analytics.referrals.columns.na", { defaultValue: "N/A" })}
                    </span>
                  )}
                </td>
                <td className="tw-py-4 tw-px-4 tw-text-sm tw-font-medium tw-text-gray-900 tw-text-right tw-whitespace-nowrap">
                  {formatCommission(referral.amount, referral.currency, referral.claim_type)}
                </td>
              </motion.tr>
            ))}
            {referrals.length === 0 && (
              <tr>
                <td colSpan="8" className="tw-py-8 tw-text-center tw-text-gray-500">{t("affiliate.analytics.referrals.empty")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="tw-flex tw-justify-between tw-items-center tw-mt-4">
          <div className="tw-text-sm tw-text-gray-600">
            {t("affiliate.analytics.referrals.showing", { defaultValue: "Showing {{start}} to {{end}} of {{total}} entries", start: Math.min(currentPage * pageSize + 1, total), end: Math.min((currentPage + 1) * pageSize, total), total })}
          </div>
          <div className="tw-flex tw-items-center tw-gap-1">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className={`tw-px-2 tw-py-1 tw-rounded-md tw-text-sm tw-min-w-[32px] tw-h-[32px] tw-flex tw-items-center tw-justify-center ${currentPage === 0 ? "tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed" : "tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 tw-border tw-border-gray-300"}`}
              aria-label={t("affiliate.analytics.referrals.previous_page", { defaultValue: "Previous page" })}
            >
              ←
            </button>
            {getPageNumbers().map((pageNum, index) => (
              <button
                key={index}
                onClick={() => pageNum !== "..." && onPageChange(pageNum - 1)}
                disabled={pageNum === "..."}
                className={`tw-px-2 tw-py-1 tw-rounded-md tw-text-sm tw-min-w-[32px] tw-h-[32px] tw-flex tw-items-center tw-justify-center ${pageNum === currentPage + 1 ? "tw-bg-blue-500 tw-text-white" : pageNum === "..." ? "tw-bg-white tw-text-gray-400 tw-cursor-default tw-border-none" : "tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 tw-border tw-border-gray-300"}`}
              >
                {pageNum}
              </button>
            ))}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className={`tw-px-2 tw-py-1 tw-rounded-md tw-text-sm tw-min-w-[32px] tw-h-[32px] tw-flex tw-items-center tw-justify-center ${currentPage >= totalPages - 1 ? "tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed" : "tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50 tw-border tw-border-gray-300"}`}
              aria-label={t("affiliate.analytics.referrals.next_page", { defaultValue: "Next page" })}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const WithdrawalSection = ({ earningsSummary, setOpenAlertBannerMsg, t }) => {
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [balances, setBalances] = useState({
    availableBalance: "0.00",
    pendingRelease: "0.00",
    totalWithdrawn: "0.00",
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchWithdrawals = async () => {
    try {
      const { data } = await getWithdrawals();
      setWithdrawalHistory(data);
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
      setOpenAlertBannerMsg(t("affiliate.withdraw.history.error", { defaultValue: "Failed to load withdrawal history" }));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBalances = async () => {
    try {
      const { data } = await getAffiliateBalances();
      setBalances(data);
    } catch (error) {
      console.error("Error fetching balances:", error);
      setOpenAlertBannerMsg(t("affiliate.withdraw.balances.error", { defaultValue: "Failed to load balance information" }));
    }
  };

  useEffect(() => {
    fetchWithdrawals();
    fetchBalances();
  }, []);

  const hasPendingWithdrawal = withdrawalHistory.some((w) => w.status === "pending");

  return (
    <section className="tw-mb-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
            <h2 className="tw-text-2xl tw-font-semibold tw-text-deepskyblue">{t("affiliate.withdraw.title")}</h2>
            <button
              onClick={() => setShowWithdrawalModal(true)}
              disabled={Number(balances.availableBalance) === 0 || hasPendingWithdrawal}
              className={`tw-bg-[deepskyblue] tw-text-white tw-px-8 tw-py-2.5 tw-rounded-full tw-font-medium hover:tw-shadow-lg tw-transition-all ${Number(balances.availableBalance) === 0 || hasPendingWithdrawal ? "tw-opacity-50 tw-cursor-not-allowed" : "hover:tw-bg-[#00a3d9]"}`}
              title={
                hasPendingWithdrawal
                  ? t("affiliate.withdraw.pending_title", { defaultValue: "You have a pending withdrawal request" })
                  : Number(balances.availableBalance) === 0
                    ? t("affiliate.withdraw.no_earnings", { defaultValue: "No earnings available for withdrawal" })
                    : t("affiliate.withdraw.request", { defaultValue: "Request withdrawal" })
              }
            >
              {t("affiliate.withdraw.withdraw_button")}
            </button>
          </div>
          {hasPendingWithdrawal && (
            <div className="tw-mb-6 tw-p-4 tw-bg-blue-50 tw-text-blue-700 tw-rounded-lg tw-flex tw-items-start tw-gap-3">
              <AlertCircle className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-mt-0.5" />
              <div>
                <p className="tw-font-medium">{t("affiliate.withdraw.pending_title")}</p>
                <p className="tw-text-sm tw-mt-1">{t("affiliate.withdraw.pending_message", { defaultValue: "You have a withdrawal request being processed. Please wait for it to be completed before making another request." })}</p>
              </div>
            </div>
          )}
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-mb-8">
            <motion.div whileHover={{ scale: 1.02 }} className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-p-6 tw-rounded-xl tw-shadow-sm tw-text-white">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                <div className="tw-bg-white/10 tw-p-2 tw-rounded-lg">
                  <DollarSign className="tw-text-white tw-w-5 tw-h-5" />
                </div>
                <h3>{t("affiliate.withdraw.available_balance")}</h3>
              </div>
              <p className="tw-text-2xl tw-font-bold">${balances.availableBalance}</p>
              <p className="tw-text-sm tw-text-blue-100">{t("affiliate.withdraw.ready_to_withdraw", { defaultValue: "Ready to withdraw" })}</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                <div className="tw-bg-purple-50 tw-p-2 tw-rounded-lg">
                  <History className="tw-text-purple-500 tw-w-5 tw-h-5" />
                </div>
                <h3 className="tw-text-gray-600">{t("affiliate.withdraw.pending_release", { defaultValue: "Pending Release" })}</h3>
              </div>
              <p className="tw-text-2xl tw-font-bold">${balances.pendingRelease}</p>
              <p className="tw-text-sm tw-text-gray-500">{t("affiliate.withdraw.pending_description", { defaultValue: "Rewards will be ready for withdrawal 1 day after the user's trip ends" })}</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100">
              <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
                <div className="tw-bg-green-50 tw-p-2 tw-rounded-lg">
                  <Banknote className="tw-text-green-500 tw-w-5 tw-h-5" />
                </div>
                <h3 className="tw-text-gray-600">{t("affiliate.withdraw.total_withdrawn", { defaultValue: "Total Withdrawn" })}</h3>
              </div>
              <p className="tw-text-2xl tw-font-bold">${balances.totalWithdrawn}</p>
              <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.timeframes.all")}</p>
            </motion.div>
          </div>
          <div className="tw-mt-8">
            <h3 className="tw-text-lg tw-font-semibold tw-mb-4">{t("affiliate.withdraw.history.title")}</h3>
            <div className="tw-overflow-x-auto">
              {isLoading ? (
                <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
                  <p className="tw-text-gray-500">{t("affiliate.withdraw.history.loading", { defaultValue: "Loading withdrawal history..." })}</p>
                </div>
              ) : (
                <table className="tw-w-full tw-border-collapse">
                  <thead>
                    <tr className="tw-border-b tw-border-gray-200">
                      <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.withdraw.history.columns.id", { defaultValue: "ID" })}</th>
                      <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.withdraw.history.columns.date")}</th>
                      <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600 tw-w-[140px]">{t("affiliate.withdraw.history.columns.amount")}</th>
                      <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600 tw-w-[180px]">{t("affiliate.withdraw.history.columns.method")}</th>
                      <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.withdraw.history.columns.status")}</th>
                      <th className="tw-text-left tw-py-4 tw-px-4 tw-text-sm tw-font-semibold tw-text-gray-600">{t("affiliate.withdraw.history.columns.estimated_arrival", { defaultValue: "Estimated Arrival" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawalHistory.map((withdrawal, index) => (
                      <motion.tr
                        key={withdrawal.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50 tw-transition-colors"
                      >
                        <td className="tw-py-4 tw-px-4 tw-text-sm tw-font-medium tw-text-gray-900">{withdrawal.id}</td>
                        <td className="tw-py-4 tw-px-4 tw-text-sm tw-text-gray-600">{withdrawal.requestDate}</td>
                        <td className="tw-py-4 tw-px-4 tw-text-sm tw-font-medium tw-text-gray-900">${withdrawal.amount}</td>
                        <td className="tw-py-4 tw-px-4 tw-w-[180px]">
                          <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm ${WITHDRAWAL_METHOD_MAP[withdrawal.method]?.className || "tw-text-gray-600"}`}>
                            {WITHDRAWAL_METHOD_MAP[withdrawal.method]?.icon}
                            {WITHDRAWAL_METHOD_MAP[withdrawal.method]?.label || withdrawal.method}
                          </span>
                        </td>
                        <td className="tw-py-4 tw-px-4">
                          <span className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${withdrawal.status === "completed" ? "tw-bg-green-50 tw-text-green-600" : "tw-bg-blue-50 tw-text-blue-600"}`}>
                            {t(`affiliate.withdraw.history.status.${withdrawal.status}`)}
                          </span>
                        </td>
                        <td className="tw-py-4 tw-px-4 tw-text-sm tw-text-gray-600">{withdrawal.estimatedArrival}</td>
                      </motion.tr>
                    ))}
                    {withdrawalHistory.length === 0 && !isLoading && (
                      <tr>
                        <td colSpan="6" className="tw-text-center tw-py-8 tw-text-gray-500">{t("affiliate.withdraw.history.empty")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      {showWithdrawalModal && (
        <WithdrawalModal
          onClose={() => setShowWithdrawalModal(false)}
          availableBalance={balances.availableBalance}
          setOpenAlertBannerMsg={setOpenAlertBannerMsg}
          onSuccess={() => {
            fetchWithdrawals();
            fetchBalances();
          }}
          t={t}
        />
      )}
    </section>
  );
};

const WithdrawalModal = ({ onClose, availableBalance, setOpenAlertBannerMsg, onSuccess, t }) => {
  const [bankDetails, setBankDetails] = useState({
    accountName: "",
    accountNumber: "",
    bankName: "",
    swiftCode: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleWithdrawalRequest = async () => {
    try {
      setIsSubmitting(true);
      await requestWithdrawal({ bankDetails });
      onClose();
      setOpenAlertBannerMsg(t("affiliate.withdraw.withdraw_modal.success"));
      onSuccess();
    } catch (error) {
      console.error("Error requesting withdrawal:", error);
      setOpenAlertBannerMsg(error.response?.data?.error || t("affiliate.withdraw.withdraw_modal.error", { defaultValue: "Failed to submit withdrawal request" }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
      <motion.div ref={modalRef} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="tw-bg-white tw-rounded-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
        <h3 className="tw-text-xl tw-font-semibold tw-mb-4">{t("affiliate.withdraw.withdraw_modal.title")}</h3>
        <div>
          <div className="tw-space-y-4">
            <div className="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-mb-4">
              <p className="tw-text-blue-700 tw-font-medium">{t("affiliate.withdraw.available_balance")}</p>
              <p className="tw-text-2xl tw-font-bold tw-text-blue-900">${availableBalance}</p>
              <p className="tw-text-sm tw-text-blue-600 tw-mt-1">{t("affiliate.withdraw.withdraw_modal.via_bank", { defaultValue: "Via bank transfer (2-3 business days)" })}</p>
            </div>
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">{t("affiliate.withdraw.withdraw_modal.account_name", { defaultValue: "Account Holder Name" })}</label>
              <input
                type="text"
                value={bankDetails.accountName}
                onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
                placeholder={t("affiliate.withdraw.withdraw_modal.account_name_placeholder", { defaultValue: "Enter account name" })}
                className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded-md"
              />
            </div>
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">{t("affiliate.withdraw.withdraw_modal.account_number", { defaultValue: "Account Number" })}</label>
              <input
                type="text"
                value={bankDetails.accountNumber}
                onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                placeholder={t("affiliate.withdraw.withdraw_modal.account_number_placeholder", { defaultValue: "Enter account number" })}
                className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded-md"
              />
            </div>
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">{t("affiliate.withdraw.withdraw_modal.bank_name", { defaultValue: "Bank Name" })}</label>
              <input
                type="text"
                value={bankDetails.bankName}
                onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                placeholder={t("affiliate.withdraw.withdraw_modal.bank_name_placeholder", { defaultValue: "Enter bank name" })}
                className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded-md"
              />
            </div>
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">{t("affiliate.withdraw.withdraw_modal.swift_code", { defaultValue: "SWIFT Code" })}</label>
              <input
                type="text"
                value={bankDetails.swiftCode}
                onChange={(e) => setBankDetails({ ...bankDetails, swiftCode: e.target.value })}
                placeholder={t("affiliate.withdraw.withdraw_modal.swift_code_placeholder", { defaultValue: "Enter SWIFT code" })}
                className="tw-w-full tw-p-2 tw-border tw-border-gray-300 tw-rounded-md"
              />
            </div>
          </div>
          <div className="tw-mt-6 tw-flex tw-gap-3">
            <button onClick={onClose} className="tw-px-6 tw-py-2.5 tw-text-gray-700 tw-bg-gray-100 tw-rounded-full tw-font-medium hover:tw-bg-gray-200 tw-transition-all">
              {t("affiliate.withdraw.withdraw_modal.cancel")}
            </button>
            <button
              onClick={handleWithdrawalRequest}
              disabled={!bankDetails.accountName || !bankDetails.accountNumber || !bankDetails.bankName || !bankDetails.swiftCode || isSubmitting}
              className={`tw-px-6 tw-py-2.5 tw-bg-[deepskyblue] tw-text-white tw-rounded-full tw-font-medium hover:tw-shadow-lg tw-transition-all tw-flex-1 ${!bankDetails.accountName || !bankDetails.accountNumber || !bankDetails.bankName || !bankDetails.swiftCode || isSubmitting ? "tw-opacity-50 tw-cursor-not-allowed" : "hover:tw-bg-[#00a3d9]"}`}
            >
              {isSubmitting ? t("affiliate.withdraw.withdraw_modal.submitting", { defaultValue: "Submitting..." }) : t("affiliate.withdraw.withdraw_modal.confirm")}
            </button>
          </div>
        </div>
        <button onClick={onClose} className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors">✕</button>
      </motion.div>
    </div>
  );
};

const LoadingSkeleton = () => {
  const { t } = useTranslation();
  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <StickyHeader withoutCta />
      <section className="tw-relative tw-h-screen tw-overflow-hidden">
        <div className="tw-absolute tw-inset-0 tw-bg-gray-200 tw-animate-pulse" />
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
          <div className="tw-text-center tw-max-w-4xl tw-px-4">
            <div className="tw-h-12 tw-w-3/4 tw-bg-gray-300 tw-rounded-lg tw-mb-4 tw-mx-auto tw-animate-pulse" />
            <div className="tw-h-6 tw-w-1/2 tw-bg-gray-300 tw-rounded-lg tw-mx-auto tw-animate-pulse" />
          </div>
        </div>
      </section>
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-8">
        <div className="tw-space-y-8">
          <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
            <div className="tw-h-8 tw-w-1/4 tw-bg-gray-200 tw-rounded-lg tw-mb-6 tw-animate-pulse" />
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="tw-space-y-4">
                  <div className="tw-h-12 tw-w-12 tw-bg-gray-200 tw-rounded-full tw-mx-auto tw-animate-pulse" />
                  <div className="tw-h-6 tw-w-3/4 tw-bg-gray-200 tw-rounded-lg tw-mx-auto tw-animate-pulse" />
                  <div className="tw-h-4 tw-w-full tw-bg-gray-200 tw-rounded-lg tw-animate-pulse" />
                </div>
              ))}
            </div>
          </div>
          <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
            <div className="tw-h-8 tw-w-1/3 tw-bg-gray-200 tw-rounded-lg tw-mb-6 tw-animate-pulse" />
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8">
              {[1, 2].map((i) => (
                <div key={i} className="tw-space-y-4">
                  <div className="tw-h-6 tw-w-1/2 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse" />
                  <div className="tw-space-y-2">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="tw-h-4 tw-w-full tw-bg-gray-200 tw-rounded-lg tw-animate-pulse" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroSection = ({ user, navigate, t }) => {
  return (
    <section className="tw-relative tw-h-screen tw-overflow-hidden">
      <div
        className="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center tw-transform tw-scale-110"
        style={{ backgroundImage: "url('https://sotravel-agent-folder.s3.ap-southeast-1.amazonaws.com/%2Frandom/151028Copy%20of%20IMG_4012.JPG')", transform: "translateZ(-1px) scale(1)" }}
      />
      <div className="tw-absolute tw-inset-0 tw-bg-black/40" />
      <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
        <div className="tw-text-center tw-text-white tw-max-w-4xl tw-px-4">
          <h1 className="tw-text-[34px] md:tw-text-[52px] tw-font-bold tw-mb-3 tw-leading-tight" dangerouslySetInnerHTML={{ __html: t("affiliate.hero.title") }} />
          {user.influencer_type ? (
            <p className="tw-text-[18px] md:tw-text-[20px] tw-mb-5 tw-text-gray-200 tw-max-w-2xl tw-mx-auto">{t("affiliate.hero.existing_affiliate")}</p>
          ) : (
            <p className="tw-text-[18px] md:tw-text-[20px] tw-mb-5 tw-text-gray-200 tw-max-w-2xl tw-mx-auto">{t("affiliate.hero.description")}</p>
          )}
          {!user.id || !user.influencer_type ? (
            <button
              onClick={() => navigate({ pathname: "/login", search: "?reroute=/request-affiliate" })}
              className="tw-bg-[#00BFFF] tw-text-white tw-font-medium tw-py-2.5 tw-px-6 tw-rounded-full tw-text-[16px] hover:tw-bg-[#00A3D9] tw-transition tw-duration-300"
            >
              {t("affiliate.hero.cta")}
            </button>
          ) : null}
        </div>
      </div>
      <motion.div
        className="tw-absolute tw-left-0 tw-right-0 tw-bottom-8 tw-mx-auto tw-w-fit tw-flex tw-flex-col tw-items-center tw-gap-2 tw-cursor-pointer"
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <motion.p className="tw-text-white/90 tw-text-sm tw-font-medium tw-tracking-wide" animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>
          {t("affiliate.hero.scroll_text")}
        </motion.p>
        <motion.div className="tw-flex tw-flex-col tw-items-center tw-gap-1.5" animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}>
          <div className="tw-w-1.5 tw-h-1.5 tw-bg-white/90 tw-rounded-full" />
          <div className="tw-w-1.5 tw-h-1.5 tw-bg-white/70 tw-rounded-full" />
          <div className="tw-w-1.5 tw-h-1.5 tw-bg-white/50 tw-rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

const HowItWorksSection = ({ currency, t }) => {
  return (
    <section className="tw-mb-12">
      <h2 className="tw-text-2xl tw-font-semibold tw-mb-8 tw-text-deepskyblue">{t("affiliate.how_it_works.title")}</h2>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-8">
        <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm tw-text-center">
          <div className="tw-flex tw-justify-center tw-mb-4">
            <div className="tw-bg-blue-50 tw-p-4 tw-rounded-full">
              <Users className="tw-text-blue-500 tw-w-8 tw-h-8" />
            </div>
          </div>
          <h3 className="tw-text-lg tw-font-semibold tw-mb-2">{t("affiliate.how_it_works.step1.title")}</h3>
          <p className="tw-text-gray-600">{t("affiliate.how_it_works.step1.description")}</p>
        </div>
        <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm tw-text-center">
          <div className="tw-flex tw-justify-center tw-mb-4">
            <div className="tw-bg-green-50 tw-p-4 tw-rounded-full">
              <CheckCircle className="tw-text-green-500 tw-w-8 tw-h-8" />
            </div>
          </div>
          <h3 className="tw-text-lg tw-font-semibold tw-mb-2">{t("affiliate.how_it_works.step2.title")}</h3>
          <p className="tw-text-gray-600">{t("affiliate.how_it_works.step2.description", { amount: getReferralAmount(currency, "welcome") })}</p>
        </div>
        <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm tw-text-center">
          <div className="tw-flex tw-justify-center tw-mb-4">
            <div className="tw-bg-purple-50 tw-p-4 tw-rounded-full">
              <DollarSign className="tw-text-purple-500 tw-w-8 tw-h-8" />
            </div>
          </div>
          <h3 className="tw-text-lg tw-font-semibold tw-mb-2">{t("affiliate.how_it_works.step3.title")}</h3>
          <p className="tw-text-gray-600">{t("affiliate.how_it_works.step3.description")}</p>
        </div>
      </div>
    </section>
  );
};

const BenefitsSection = ({ currency, t }) => {
  return (
    <section className="tw-mb-12">
      <h2 className="tw-text-2xl tw-font-semibold tw-mb-8 tw-text-deepskyblue">{t("affiliate.benefits.title")}</h2>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-8">
        <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
          <h3 className="tw-text-lg tw-font-semibold tw-mb-4">{t("affiliate.benefits.for_you")}</h3>
          <ul className="tw-space-y-3">
            {t("affiliate.benefits.benefits_you", { returnObjects: true }).map((benefit, index) => (
              <li key={index} className="tw-flex tw-items-center tw-gap-2">
                <CheckCircle className="tw-text-green-500 tw-w-5 tw-h-5" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
          <h3 className="tw-text-lg tw-font-semibold tw-mb-4">{t("affiliate.benefits.for_audience")}</h3>
          <ul className="tw-space-y-3">
            {t("affiliate.benefits.benefits_audience", { returnObjects: true, amount: getReferralAmount(currency, "welcome") }).map((benefit, index) => (
              <li key={index} className="tw-flex tw-items-center tw-gap-2">
                <CheckCircle className="tw-text-green-500 tw-w-5 tw-h-5" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const AffiliateLinkSection = ({ inviteUrl, inviteMessage, changeCopyColor, copyIconColor, t }) => {
  return (
    <section className="tw-mb-8">
      <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
        <h2 className="tw-text-xl tw-font-semibold tw-mb-4 tw-text-deepskyblue">{t("affiliate.affiliate_link.title")}</h2>
        <p className="tw-text-gray-600 tw-mb-4">{t("affiliate.affiliate_link.share_text")}</p>
        <CopyToClipboard text={inviteMessage} onCopy={changeCopyColor}>
          <button className="tw-h-12 tw-py-2 tw-px-4 tw-border tw-border-solid tw-rounded-md tw-font-normal tw-text-[15px] tw-font-manrope tw-text-black tw-flex tw-items-center tw-justify-between tw-bg-white tw-w-full" style={{ borderColor: copyIconColor }}>
            <span className="tw-h-full tw-w-[90%] tw-flex tw-items-center tw-justify-start tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis tw-inline-block tw-text-left" style={{ color: copyIconColor }}>
              {inviteUrl}
            </span>
            <ContentCopyOutlinedIcon className="tw-text-[22px]" style={{ color: copyIconColor }} />
          </button>
        </CopyToClipboard>
      </div>
    </section>
  );
};

const AnalyticsStatsSection = ({ analyticsData, timeframe, earningsSummary, t }) => {
  return (
    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4 tw-mb-8">
      <motion.div whileHover={{ scale: 1.02 }} className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <div className="tw-bg-blue-50 tw-p-2 tw-rounded-lg">
            <Activity className="tw-text-blue-500 tw-w-5 tw-h-5" />
          </div>
          <h3 className="tw-text-gray-600">{t("affiliate.analytics.stats.clicks")}</h3>
        </div>
        <p className="tw-text-2xl tw-font-bold">{earningsSummary.totalClicks}</p>
        <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.timeframes.all")}</p>
      </motion.div>
      <motion.div whileHover={{ scale: 1.02 }} className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <div className="tw-bg-green-50 tw-p-2 tw-rounded-lg">
            <Users className="tw-text-green-500 tw-w-5 tw-h-5" />
          </div>
          <h3 className="tw-text-gray-600">{t("affiliate.analytics.stats.signups")}</h3>
        </div>
        <p className="tw-text-2xl tw-font-bold">{earningsSummary.totalSignups}</p>
        <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.timeframes.all")}</p>
      </motion.div>
      <motion.div whileHover={{ scale: 1.02 }} className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <div className="tw-bg-purple-50 tw-p-2 tw-rounded-lg">
            <BarChart2 className="tw-text-purple-500 tw-w-5 tw-h-5" />
          </div>
          <h3 className="tw-text-gray-600">{t("affiliate.analytics.stats.bookings")}</h3>
        </div>
        <p className="tw-text-2xl tw-font-bold">{earningsSummary.totalBookings}</p>
        <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.timeframes.all")}</p>
      </motion.div>
      {/* <motion.div whileHover={{ scale: 1.02 }} className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <div className="tw-bg-yellow-50 tw-p-2 tw-rounded-lg">
            <LineChartIcon className="tw-text-yellow-500 tw-w-5 tw-h-5" />
          </div>
          <h3 className="tw-text-gray-600">{t("affiliate.analytics.stats.avg_booking_value", { defaultValue: "Avg. Booking Value" })}</h3>
        </div>
        <p className="tw-text-2xl tw-font-bold">${earningsSummary.totalBookings > 0 ? (parseFloat(earningsSummary.totalEarnings) / earningsSummary.totalBookings).toFixed(2) : "0.00"}</p>
        <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.stats.per_booking", { defaultValue: "Per booking" })}</p>
      </motion.div> */}
    </div>
  );
};

const AnalyticsSection = ({ isLoading, timeframe, setTimeframe, analyticsData, earningsSummary, referrals, referralTotal, currentPage, onPageChange, pageSize, t }) => {
  if (isLoading) {
    return (
      <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-h-64 tw-space-y-4">
        <p className="tw-text-gray-500 tw-font-medium tw-animate-pulse">{t("affiliate.analytics.loading", { defaultValue: "Loading analytics..." })}</p>
      </div>
    );
  }

  return (
    <section className="tw-mb-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="tw-mb-8">
          <h2 className="tw-text-2xl tw-font-semibold tw-text-deepskyblue tw-mb-4">{t("affiliate.analytics.title")}</h2>
          <AnalyticsStatsSection analyticsData={analyticsData} timeframe={timeframe} earningsSummary={earningsSummary} t={t} />
        </div>
        <div className="tw-mb-8">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
            <h2 className="tw-text-2xl tw-font-semibold tw-text-deepskyblue">{t("affiliate.analytics.time_based_title", { defaultValue: "Time-based Analytics" })}</h2>
            <Select
              value={timeframe}
              onChange={(option) => setTimeframe(option.value)}
              options={[
                { value: "daily", label: t("affiliate.analytics.timeframes.7d") },
                { value: "weekly", label: t("affiliate.analytics.timeframes.30d") },
                { value: "monthly", label: t("affiliate.analytics.timeframes.90d") },
                { value: "all_time", label: t("affiliate.analytics.timeframes.all") },
              ]}
              className="tw-min-w-[120px]"
            />
          </div>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
            <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
              <h3 className="tw-text-lg tw-font-semibold tw-mb-4">{t("affiliate.analytics.earnings.title")}</h3>
              <div style={{ width: "100%", height: 300 }}>
                {analyticsData[timeframe]?.length > 0 && analyticsData[timeframe].some((data) => parseFloat(data.earningsCash) > 0 || parseFloat(data.earningsCredits) > 0) ? (
                  <ResponsiveContainer>
                    <ComposedChart data={analyticsData[timeframe]} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorEarningsCash" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00BFFF" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#00BFFF" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorEarningsCredits" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip formatter={(value, name) => name === "Cash Earnings" ? [`$${value}`, t("affiliate.analytics.earnings.cash", { defaultValue: "Cash Earnings" })] : [`${value} credits`, t("affiliate.analytics.earnings.credits", { defaultValue: "Credit Earnings" })]} />
                      <Legend />
                      <Area type="monotone" dataKey="earningsCash" name={t("affiliate.analytics.earnings.cash", { defaultValue: "Cash Earnings" })} stroke="#00BFFF" fillOpacity={1} fill="url(#colorEarningsCash)" />
                      <Line type="monotone" dataKey="earningsCredits" name={t("affiliate.analytics.earnings.credits", { defaultValue: "Credit Earnings" })} stroke="#82ca9d" strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-gray-500">
                    <LineChartIcon className="tw-w-12 tw-h-12 tw-mb-3 tw-opacity-50" />
                    <p className="tw-text-lg tw-font-medium">{t("affiliate.analytics.earnings.no_data", { defaultValue: "No earnings data yet" })}</p>
                    <p className="tw-text-sm tw-mt-1">{t("affiliate.analytics.earnings.no_data_message", { defaultValue: "Start sharing your affiliate link to earn rewards" })}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm">
              <h3 className="tw-text-lg tw-font-semibold tw-mb-4">{t("affiliate.analytics.conversion_title", { defaultValue: "Conversion Metrics" })}</h3>
              <div style={{ width: "100%", height: 300 }}>
                {analyticsData[timeframe]?.length > 0 && analyticsData[timeframe].some((data) => parseInt(data.clicks) > 0 || parseInt(data.signups) > 0 || parseInt(data.bookings) > 0) ? (
                  <ResponsiveContainer>
                    <LineChart data={analyticsData[timeframe]}>
                      <XAxis dataKey="date" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="clicks" stroke="#8884d8" name={t("affiliate.analytics.stats.clicks")} />
                      <Line type="monotone" dataKey="signups" stroke="#82ca9d" name={t("affiliate.analytics.stats.signups")} />
                      <Line type="monotone" dataKey="bookings" stroke="#ffc658" name={t("affiliate.analytics.stats.bookings")} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-gray-500">
                    <BarChart2 className="tw-w-12 tw-h-12 tw-mb-3 tw-opacity-50" />
                    <p className="tw-text-lg tw-font-medium">{t("affiliate.analytics.conversion_no_data", { defaultValue: "No conversion data yet" })}</p>
                    <p className="tw-text-sm tw-mt-1">{t("affiliate.analytics.conversion_no_data_message", { defaultValue: "Share your link to start tracking conversions" })}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-p-6">
          <h3 className="tw-text-xl tw-font-semibold tw-mb-4">{t("affiliate.analytics.referrals.title")}</h3>
          <ReferralsTable referrals={referrals} total={referralTotal} currentPage={currentPage} onPageChange={onPageChange} pageSize={pageSize} t={t} />
        </div>
      </motion.div>
    </section>
  );
};

export default AffiliateProgram;